// Batch 011 / ADR-9 — the transfer domain speaks the NEW contract directly
// (batches/011/transfer.proto, refrozen 2026-08-07 `8dc1a6d4346222ae`).
//
// This slice replaces fourteen legacy BFF calls that were spread across
// clinician.api and patient.api. Those definitions are gone, not wrapped: this
// is an adapter-free migration, per the repo's second baseline (CLAUDE.md
// §重构目标 2 — never reshape the new contract back into the legacy shape).
//
// ── the two crossings this file owns ──
//
// ① SLUGS. Every enum value crosses here and nowhere else, through the explicit
//    tables in transfer-slug.ts. Renderers keep receiving the legacy bare
//    strings they have always indexed by, so no render code learns about slugs.
// ② CASE. Responses are protojson lowerCamel; request BODIES are snake_case
//    (proto field names). Unlike the patient domain, nothing here is converted
//    to snake_case for consumers — the transfer readers are all being rewritten
//    by this batch, so they read the wire as it arrives, the way batch 009's
//    measurement surface does. See TransferPage in api.types.ts for the trap
//    that keeps the two envelope types apart.
import { createApi } from '@reduxjs/toolkit/query/react';

import authedQuery from '@/services/authed.query';
import { patientApi } from '@/services/patient.api';
import { severitySlugToBare, sexSlugToBare } from '@/services/patient-slug';
import {
  ApiTags,
  ApplyTransferBody,
  DecideTransferBody,
  ListTransferEventsResponse,
  ListTransfersResponse,
  PutTransferConsentBody,
  TransferConsentResponse,
  TransferDetailResponse,
  TransferEvent,
  TransferSummary,
} from '@/services/api.types';
import {
  eventLogTypeFromKind,
  transferDirectionSlugToBare,
  transferEventKindSlugToBare,
  transferOptionSlugToBare,
  transferPillStatus,
  transferStatusSlugToBare,
} from '@/services/transfer-slug';
import { latestReferralOf } from '@/utils/transfer-referral';

// The two query-parameter sets the inbox surfaces need. Both hit the SAME
// endpoint — the badge/banner count is the pending subset's totalRecords, and
// the list itself is every incoming ticket (ADR-9 ③).
export const INCOMING = '#TransferDirection.Incoming';
export const PENDING = '#TransferStatus.Pending';

export type ListTransfersParams = {
  direction?: string;
  status?: string;
  page?: number;
  size?: number;
};

// One row across the slug boundary. `status` becomes the pill vocabulary and
// carries the restored `deceased` form (ADR-9 ③): a ticket that ended because
// the patient died shows the legacy deceased pill, which the ticket status
// alone cannot express — hence `last_option` travelling beside it.
const summaryFromWire = (row: any): TransferSummary => {
  const status = transferStatusSlugToBare(row?.status);
  const lastOption = transferOptionSlugToBare(row?.lastOption);
  return {
    ...row,
    direction: transferDirectionSlugToBare(row?.direction),
    lastOption,
    status: transferPillStatus(status, lastOption),
    // `latest_alarm` crosses as the `#Severity.*` slug (transfer.proto:141,
    // settled at the 2026-08-07 batch gate) and is un-slugged HERE, through the
    // client's single severity table. Slug-only on purpose: see the note where
    // that used to be decided, transfer-slug.ts, and patient-slug.ts:69-71.
    latestAlarm: severitySlugToBare(row?.latestAlarm),
  };
};

// ⚠️ `logType` is derived HERE, from the SLUG, and carried on the row.
//
// It cannot be recovered downstream: the bare vocabulary is many-to-one, so
// `#TransferEvent.Accepted` and `#TransferEvent.ConsentGranted` both become
// 'accept'. The log modal needs to tell them apart — one is "admitted the
// patient", the other is "granted data-sharing consent", with different icons
// and different copy. Computing both halves at the single crossing point is
// what stops them from ever disagreeing.
const eventFromWire = (event: any): TransferEvent => ({
  ...event,
  kind: transferEventKindSlugToBare(event?.kind),
  logType: eventLogTypeFromKind(event?.kind),
  option: transferOptionSlugToBare(event?.option),
});

export const transferApi = createApi({
  reducerPath: 'transferApi',
  tagTypes: [
    ApiTags.TransferList,
    ApiTags.TransferDetail,
    ApiTags.TransferConsent,
    ApiTags.TransferEvents,
  ],
  baseQuery: authedQuery,
  endpoints: builder => ({
    // ── the sending side ──

    // POST /v1/patients/{patient}/transfers.
    //
    // `patient` is the route UUID, passed through as the string it is. The
    // legacy page coerced it with `Number()`, which is the batch-009 defect
    // this contract's `string.uuid` rule makes impossible to miss.
    applyTransfer: builder.mutation<
      { id?: string },
      { patient: string; body: ApplyTransferBody }
    >({
      query: ({ patient, body }) => ({
        url: `/v1/patients/${patient}/transfers`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: ApiTags.TransferList, id: 'LIST' }],
      // PD-3033: a sent transfer also changes the patient's detail, the paged
      // patient list and the patient's transfer log, and the operator lands on
      // the detail page right after. The detail and the list live in the
      // PATIENT slice and RTK Query tags never cross slices, so they are
      // dispatched at that slice explicitly (measurement.api's ack precedent).
      //
      // Only after `queryFulfilled`. That is also why the log is dispatched
      // here rather than listed in `invalidatesTags`: RTK evaluates
      // `invalidatesTags` for a rejected mutation too, and a rejected send
      // changed nothing server-side, so nothing may refetch. The await must be
      // caught — an uncaught one takes the process down.
      //
      // ADR-B5 keeps the paged list un-invalidated by the pin toggle and the
      // measurement ack only. A transfer write moves the patient's status,
      // which the list shows.
      async onQueryStarted({ patient }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          patientApi.util.invalidateTags([
            { type: ApiTags.Patient, id: patient },
            { type: ApiTags.PatientList, id: 'LIST' },
          ])
        );
        dispatch(
          transferApi.util.invalidateTags([
            { type: ApiTags.TransferEvents, id: patient },
          ])
        );
      },
    }),

    // ── the receiving side ──

    listTransfers: builder.query<ListTransfersResponse, ListTransfersParams>({
      query: params => ({
        url: '/v1/transfers',
        method: 'GET',
        params,
      }),
      transformResponse: (response: any): ListTransfersResponse => ({
        ...response,
        transfers: (response?.transfers ?? []).map(summaryFromWire),
      }),
      providesTags: [{ type: ApiTags.TransferList, id: 'LIST' }],
    }),

    getTransfer: builder.query<TransferDetailResponse, string>({
      query: (id: string) => ({ url: `/v1/transfers/${id}`, method: 'GET' }),
      transformResponse: (response: any): TransferDetailResponse => ({
        ...response,
        // The sending side's group name lives HERE, on the embedded summary —
        // the top-level field 7 that used to hold it is `reserved`.
        transfer: response?.transfer
          ? summaryFromWire(response.transfer)
          : undefined,
        // `sex` is an enum like any other on this contract and crosses as the
        // `#Sex.*` slug. It gets the patient domain's own table rather than a
        // second one (patient-slug.ts, the same function patient.api uses), and
        // it matters more than it looks: the header renders this value AS AN
        // i18n KEY, so an unmapped slug is printed to the clinician verbatim.
        sex: sexSlugToBare(response?.sex),
        events: (response?.events ?? []).map(eventFromWire),
      }),
      providesTags: (result, error, id) => [
        { type: ApiTags.TransferDetail, id },
      ],
    }),

    // POST /v1/transfers/{id}/decision — accept / reject / end.
    decideTransfer: builder.mutation<
      void,
      { id: string; body: DecideTransferBody }
    >({
      query: ({ id, body }) => ({
        url: `/v1/transfers/${id}/decision`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: ApiTags.TransferList, id: 'LIST' },
        { type: ApiTags.TransferDetail, id },
      ],
      // PD-3033: every decision (accept, reject, end) also changes the paged
      // patient list, the patient's detail and the patient's transfer log.
      // Same rule as applyTransfer: dispatched, and only after `queryFulfilled`.
      //
      // The argument carries the TRANSFER id, not the patient's, so the detail
      // and the log are expired by type, without an id. That matches every
      // cached entry of the type: the one on screen is refetched, unsubscribed
      // ones are dropped and fetched fresh on their next visit. At most one
      // patient's detail and log are on screen at a time (the detail page holds
      // both, the pending-ticket page neither), so this costs one request each.
      // measurement.api's ackMeasurements expires its windows the same way.
      //
      // This replaces `{ TransferEvents, 'LIST' }`, which stood in
      // `invalidatesTags` above and matched nothing: the log is provided per
      // patient id, never as 'LIST'. Made to match there, it would also fire
      // on a rejected decision.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          patientApi.util.invalidateTags([
            ApiTags.Patient,
            { type: ApiTags.PatientList, id: 'LIST' },
          ])
        );
        dispatch(transferApi.util.invalidateTags([ApiTags.TransferEvents]));
      },
    }),

    // PUT /v1/transfers/{id}/read — the TICKET's read watermark, which is a
    // different watermark from the patient event stream's below. Both exist
    // because a receiver reading a pending ticket has no local patient yet.
    readTransfer: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `/v1/transfers/${id}/read`,
        method: 'PUT',
        // Empty object, not bodyless: the BE kratos codec needs a Content-Type
        // to decode the request (same reason as organization.api's sync call).
        body: {},
      }),
      invalidatesTags: (result, error, id) => [
        { type: ApiTags.TransferDetail, id },
      ],
    }),

    // ── the patient's transfer history (both sides see their own) ──

    listTransferEvents: builder.query<ListTransferEventsResponse, string>({
      query: (patient: string) => ({
        url: `/v1/patients/${patient}/transfer-events`,
        method: 'GET',
      }),
      transformResponse: (response: any): ListTransferEventsResponse => {
        const events = (response?.events ?? []).map(eventFromWire);
        return {
          ...response,
          events,
          // The patient's current referral, derived HERE so the stream is read
          // in one place. It restores the legacy detail response's
          // `allow_discharged` / `discharged_id` pair — the effect, not the
          // shape (utils/transfer-referral.ts carries the legacy backend's own
          // computation and why this side can reproduce it).
          //
          // ⚠️ It must run AFTER eventFromWire, not on the raw response: the
          // derivation reads the BARE `kind` and the derived `logType`, and on
          // the wire both are still `#TransferEvent.*` slugs.
          latestReferral: latestReferralOf(events),
        };
      },
      providesTags: (result, error, patient) => [
        { type: ApiTags.TransferEvents, id: patient },
      ],
    }),

    readTransferEvents: builder.mutation<void, string>({
      query: (patient: string) => ({
        url: `/v1/patients/${patient}/transfer-events/read`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (result, error, patient) => [
        { type: ApiTags.TransferEvents, id: patient },
      ],
    }),

    // ── consent (ADR-7) ──
    //
    // This pair replaces the legacy share endpoints, which were keyed by a
    // numeric patient id the new system does not have — so the 009 panel was
    // calling them with `Number(uuid)` = NaN and every answer was an error.
    // `consentDataStatus.isAllowed` was therefore permanently false, which made
    // the whole transfer entry point unreachable (ADR-9 ① / F7-2).
    getTransferConsent: builder.query<TransferConsentResponse, string>({
      query: (patient: string) => ({
        url: `/v1/patients/${patient}/transfer-consent`,
        method: 'GET',
      }),
      providesTags: (result, error, patient) => [
        { type: ApiTags.TransferConsent, id: patient },
      ],
    }),

    putTransferConsent: builder.mutation<
      TransferConsentResponse,
      { patient: string; body: PutTransferConsentBody }
    >({
      query: ({ patient, body }) => ({
        url: `/v1/patients/${patient}/transfer-consent`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { patient }) => [
        { type: ApiTags.TransferConsent, id: patient },
        // Flipping consent writes an event, so the patient's history is stale.
        { type: ApiTags.TransferEvents, id: patient },
      ],
    }),
  }),
});

export const {
  useApplyTransferMutation,
  useListTransfersQuery,
  useLazyListTransfersQuery,
  useGetTransferQuery,
  useDecideTransferMutation,
  useReadTransferMutation,
  useListTransferEventsQuery,
  useLazyListTransferEventsQuery,
  useReadTransferEventsMutation,
  useGetTransferConsentQuery,
  useLazyGetTransferConsentQuery,
  usePutTransferConsentMutation,
} = transferApi;

export default transferApi;
